import React, { ReactNode } from 'react';
import { Panel } from '@uds/panel';
import PaddingBox from '@uds/padding-box';
import { Product, selectors as prodSelectors } from '@uds/product';
import { Colorway as ColorwayType } from '@uds/theme-utils';
import Colorway from '@uds/colorway';
import { LegalText } from 'components/common/ProductPrice';
import { CTA } from 'core/ribbons/cta';
import { ImagesWithAspectRatios } from 'core/ribbons/primitives';
import { ImageAspectRatio } from 'domain/constants/imageAspectRatio';
import { PricingStrategy } from 'presentation/components/common/ProductPrice/constants';

import { FeatureProps } from './ItemFeature';
import useStylesheet, { getClassNames } from './Item.stylesheet';
import Features from './ItemFeatures';
import Price from './ItemPrice';
import Details from './ItemDetails';
import Header from './ItemHeader';

export type ProductDetailsType = {
  tagline: string;
  productName: string;
};

interface OuterProps {
  colorway: ColorwayType;
  product?: Product;
  features: Array<FeatureProps>;
  hBaseline: number;
  illustration?: string;
  imageUrl?: string;
  imageAspectRatio?: ImageAspectRatio;
  imagesWithAspectRatios?: ImagesWithAspectRatios;
  ctas: Array<CTA>;
  pricingStrategy: PricingStrategy;
  endorse: ReactNode;
  productId: string;
  index: number;
  id: string;
  visibleFeatureLimit: number;
  maxFeatureLimit: number;
  hasMoreFeatures: boolean;
  showMoreFeatures: boolean;
  onShowMoreClick: (evt: any) => void;
  descRef: any;
  hasFeaturedProduct?: boolean;
  text?: ProductDetailsType;
}

const PureSalesboardItem = ({
  colorway,
  features,
  endorse,
  hBaseline,
  illustration,
  imageUrl,
  imageAspectRatio,
  imagesWithAspectRatios,
  product = {} as Product,
  ctas,
  pricingStrategy,
  productId,
  visibleFeatureLimit,
  maxFeatureLimit,
  hasMoreFeatures,
  showMoreFeatures,
  descRef,
  id,
  index,
  onShowMoreClick,
  hasFeaturedProduct,
  text,
}: OuterProps) => {
  const name = text?.productName ?? prodSelectors.getName({ product });
  const tagline = text?.tagline ?? prodSelectors.getTagline({ product });

  const isEndorsed = Boolean(endorse);
  const { styles } = useStylesheet({
    colorway,
  });
  const borderless = [ColorwayType.LIGHT, ColorwayType.NEUTRAL].indexOf(colorway) === -1;
  const classNames = getClassNames({
    colorway,
    hasFeaturedProduct,
    isEndorsed,
  });
  return (
    <div data-selector="salesboard-item" data-value={productId}>
      <Panel border={!borderless} className={classNames.root}>
        <Header
          colorway={colorway}
          illustration={illustration}
          imageUrl={imageUrl}
          imageAspectRatio={imageAspectRatio}
          imagesWithAspectRatios={imagesWithAspectRatios}
          isEndorsed={isEndorsed}
          endorse={endorse}
        />
        <Colorway colorway={ColorwayType.LIGHT}>
          <div ref={descRef}>
            <PaddingBox className={classNames.body}>
              <Details hBaseline={hBaseline} title={name} text={tagline} />
              <Price ctas={ctas} id={id} index={index} product={product} pricingStrategy={pricingStrategy} />
            </PaddingBox>
          </div>
          <Features
            colorway={colorway}
            isEndorsed={isEndorsed}
            features={features}
            onShowMoreClick={onShowMoreClick}
            showMoreFeatures={showMoreFeatures}
            hasMoreFeatures={hasMoreFeatures}
            visibleFeatureLimit={visibleFeatureLimit}
            maxFeatureLimit={maxFeatureLimit}
            id={id}
            index={index}
          />
        </Colorway>
      </Panel>
      <div style={styles.legalText}>
        <LegalText product={product} pricingStrategy={pricingStrategy} />
      </div>
    </div>
  );
};

PureSalesboardItem.displayName = 'SalesboardItem';

export default PureSalesboardItem;
